import Luminary from '@lumen5/luminary';

import { type LintFinding, runVideoLint } from '../lint/videoLinter';

import { computeSceneShortIds } from './scene-addressing';
import { referenceCopyOf } from './scene-copy';

import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { GetRootState } from 'js/src/libs/RootState';
import { awaitCanPlayWithTimeout } from 'js/src/luminary/can-play-utils';

/**
 * Lint results by reference, keyed on its scene ids: a reference is described again on the agent's
 * second fetch (the one that applies its styling), and rendering every scene twice would double the
 * slowest part of the fetch.
 */
const referenceLintCache = new Map<string, Promise<LintFinding[]>>();

/**
 * Every lint check, on a render-enabled copy of the reference that loads media and fonts — the
 * engine the reference is described on has no renderer, and `text_contrast` renders frames.
 * A copy that never becomes playable is linted as far as it loaded; one that can't be created at all
 * (no WebGL) falls back to the described engine, where only the rendered checks are lost.
 */
function lintReference(luminary: Luminary, getState: GetRootState): Promise<LintFinding[]> {
    const key = luminary.scenes.map((scene) => scene.id).join(',');
    const cached = referenceLintCache.get(key);
    if (cached) {
        return cached;
    }
    const findings = (async () => {
        let rendered: Luminary | null = null;
        try {
            rendered = new Luminary({
                fetchResources: { audio: false, media: true, transitions: false, fonts: true },
                disableRenderer: false,
                resolutionValue: luminary.resolutionValue,
            });
            rendered.load(luminary.save());
            await awaitCanPlayWithTimeout(rendered).catch(() => undefined);
        } catch {
            rendered?.destroy();
            rendered = null;
        }
        try {
            return (await runVideoLint(rendered ?? luminary, getState)).findings;
        } finally {
            rendered?.destroy();
        }
    })();
    referenceLintCache.set(key, findings);
    return findings;
}

/** The findings each fetched reference scene has, by its full scene id. */
const referenceFindingsByScene = new Map<string, LintFinding[]>();

function rememberReferenceFindings(findings: LintFinding[]): void {
    for (const finding of findings) {
        if (typeof finding.sceneId !== 'string') {
            continue;
        }
        const sceneFindings = referenceFindingsByScene.get(finding.sceneId) ?? [];
        sceneFindings.push(finding);
        referenceFindingsByScene.set(finding.sceneId, sceneFindings);
    }
}

/**
 * Whether a finding on the video being edited is one its reference already has: it is on a scene
 * copied from a reference scene that fails the same check, on the same asset when the check judges
 * assets one at a time. Such a finding is the look the user asked for, not a defect to fix.
 */
export function matchesReferenceFinding(finding: LintFinding): boolean {
    if (typeof finding.sceneId !== 'string') {
        return false;
    }
    const copy = referenceCopyOf(finding.sceneId);
    if (!copy) {
        return false;
    }
    return (referenceFindingsByScene.get(copy.sourceSceneId) ?? []).some(
        (referenceFinding) =>
            referenceFinding.check === finding.check &&
            referenceFinding.assetName === finding.assetName,
    );
}

/** Enough to show the reference's patterns without flooding the annotation. */
const MAX_LISTED_FINDINGS = 25;

/**
 * The lint findings the reference video itself has, as an annotation section telling the agent to
 * skip the same findings on its imitation. A reference is the look the user asked for, so a widow,
 * a long read or an unusual layout it already carries was accepted there, and "fixing" it on the
 * copy moves the imitation away from it. Empty when the reference lints clean.
 */
export async function describeReferenceLint(
    luminary: Luminary,
    getState: GetRootState,
): Promise<string> {
    const findings = await lintReference(luminary, getState);
    rememberReferenceFindings(findings);
    if (findings.length === 0) {
        return '';
    }

    const shortIds = computeSceneShortIds(luminary.scenes.map((scene) => scene.id));
    const locate = (sceneId: string) => {
        const design = luminary.getSceneById(sceneId)?.sceneDesign;
        return `scene_id ${shortIds.get(sceneId) ?? sceneId}${design ? ` (${design})` : ''}`;
    };
    const lines = findings.slice(0, MAX_LISTED_FINDINGS).map((finding) => {
        const sceneIds =
            finding.sceneId === undefined
                ? []
                : Array.isArray(finding.sceneId)
                  ? finding.sceneId
                  : [finding.sceneId];
        const location = sceneIds.length > 0 ? `[${sceneIds.map(locate).join(', ')}] ` : '';
        return `- ${location}${finding.check}: ${finding.message}`;
    });
    const unlisted = findings.length - lines.length;

    return [
        '## Reference Lint Findings',
        `_The reference video itself fails these ${TOOL_NAMES.lintVideo} checks. They are part of the look the user asked for, so treat them as deliberate: when ${TOOL_NAMES.lintVideo} or a review reports the same issue on your counterpart scene (same check, same kind of scene), skip it rather than changing the scene away from the reference._`,
        ...lines,
        ...(unlisted > 0 ? [`- …and ${unlisted} more of the same kinds.`] : []),
    ].join('\n');
}
